/**
 * Client crash reporting — the seam that did not exist before 2026-08-21.
 *
 * WHY: when the browser broke, nothing ever reached the server. Measured on that
 * date: 0 error boundaries, 0 window.onerror handlers, 0 unhandledrejection
 * handlers, 0 client-error endpoints — and prod silences console.log/debug/info/
 * warn (see src/index.js), so even deliberate warnings were invisible. "It
 * crashed" was the most detail this system could produce about any client fault,
 * which is why the same symptom was investigated from scratch four times.
 *
 * Deliberately uses raw `fetch`, NOT the app's axios instance: axios carries
 * auth-refresh and offline interceptors, and if one of those is what threw, a
 * reporter routed through it would recurse or be swallowed by the very layer it
 * is trying to report on. Auth rides on httpOnly cookies, so `credentials:
 * 'same-origin'` is all this needs.
 */

const ENDPOINT = '/api/client-health/errors/';

/** Per-tab cap. A crash loop must not become a request flood against prod. */
const MAX_REPORTS_PER_SESSION = 10;
let reportsSent = 0;

/** Suppress duplicates — a render loop can throw the same error many times. */
const seen = new Set();

/** Values that must never leave the browser, whatever the payload picked up. */
const SENSITIVE = /(authorization|token|password|secret|api[-_]?key|cookie)/i;

/**
 * Strip anything that looks like a credential, and cap length.
 * PREMISE: crash payloads are diagnostic text only. If this ever starts carrying
 * structured request bodies, re-check this scrub against the new shape.
 */
function scrub(value, maxLength = 4000) {
  if (typeof value !== 'string') return '';
  return value
    .split('\n')
    .filter((line) => !SENSITIVE.test(line))
    .join('\n')
    .slice(0, maxLength);
}

function fingerprint(payload) {
  return [payload.name, payload.message, payload.source].join('|');
}

/**
 * Send one crash record. Never throws, never blocks the caller, never retries —
 * the reporter must not be able to become the outage it is reporting on.
 *
 * @returns {Promise<string|null>} the server-assigned reference id, or null.
 */
export async function reportClientError({
  name,
  message,
  stack,
  componentStack,
  source,
  bundle,
} = {}) {
  try {
    if (reportsSent >= MAX_REPORTS_PER_SESSION) return null;

    const payload = {
      name: scrub(name, 200) || 'Error',
      message: scrub(message, 1000),
      stack: scrub(stack),
      component_stack: scrub(componentStack),
      source: scrub(source, 60) || 'unknown',
      url: window.location.pathname + window.location.search,
      user_agent: scrub(navigator.userAgent, 400),
      bundle: scrub(bundle, 200),
    };

    const key = fingerprint(payload);
    if (seen.has(key)) return null;
    seen.add(key);
    reportsSent += 1;

    const res = await fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res || !res.ok) return null;

    const data = await res.json().catch(() => null);
    return (data && data.reference) || null;
  } catch (e) {
    // Swallowed on purpose: the endpoint being down must still leave the user
    // with a working error card. This is the one place a silent catch is correct.
    return null;
  }
}

/**
 * Catch the failures a React error boundary cannot see: errors thrown outside
 * the render tree, and rejected promises nobody handled.
 */
export function installGlobalErrorHandlers({ getBundle } = {}) {
  const bundle = () => {
    try {
      return typeof getBundle === 'function' ? getBundle() : '';
    } catch (e) {
      return '';
    }
  };

  window.addEventListener('error', (event) => {
    if (!event) return;
    const err = event.error;
    reportClientError({
      name: (err && err.name) || 'Error',
      message: (err && err.message) || event.message,
      stack: err && err.stack,
      source: 'window.onerror',
      bundle: bundle(),
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (!event) return;
    const reason = event.reason;
    reportClientError({
      name: (reason && reason.name) || 'UnhandledRejection',
      message: (reason && reason.message) || String(reason),
      stack: reason && reason.stack,
      source: 'unhandledrejection',
      bundle: bundle(),
    });
  });
}
